import { useEffect, useState } from 'react';

declare const __BUILD_ID__: string;

/** How often an open tab asks whether a newer build is live. */
const POLL_MS = 5 * 60 * 1000;

/**
 * "Amassa was updated — reload." A deploy replaces the code on the server,
 * but a tab that stays open keeps running the bundle it loaded; nothing ever
 * told it. This compares the build id baked into the running bundle with
 * /version.json (written by the build, never cached) on an interval and
 * whenever the tab comes back into view, and shows one quiet bar when they
 * differ. It never reloads by itself: someone may be halfway through a form.
 */
export function VersionNotice() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const running = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : '';
    if (!running || import.meta.env.DEV) return;
    let cancelled = false;

    async function check() {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { build } = (await res.json()) as { build?: string };
        if (!cancelled && build && build !== running) setStale(true);
      } catch {
        /* offline or mid-deploy: try again next time */
      }
    }

    const timer = window.setInterval(check, POLL_MS);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    void check();
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  if (!stale) return null;
  return (
    <div role="status" className="version-notice">
      <span>Amassa has been updated.</span>
      <button type="button" onClick={() => window.location.reload()}>
        reload
      </button>
    </div>
  );
}
